import { useEffect, useState } from 'react';
import { Alert, Button, Card, Dropdown, Table, message } from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';

import http from '../../http';
import {
    getDateString,
    getReadableDateFromTimeStamp,
    getReadableTimeFromTimeStamp,
} from '../../utilities/common';

// Hours float -> "HH:MM:SS" (e.g. 2.5 -> "02:30:00"). Null -> "-".
const formatHours = (hours) => {
    if (hours == null) return '-';
    const h = Math.floor(hours);
    const m = Math.floor((hours % 1) * 60);
    const s = Math.floor(((hours % 1) * 60 % 1) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Nested per-day sessions table (expanded row). sessions = [{start, end, duration}].
const sessionColumns = [
    {
        title: 'Starts at',
        dataIndex: 'start',
        key: 'start',
        render: (v) => getReadableTimeFromTimeStamp(v, 'hh:mm A'),
    },
    {
        title: 'Ends at',
        dataIndex: 'end',
        key: 'end',
        render: (v) => getReadableTimeFromTimeStamp(v, 'hh:mm A'),
    },
    {
        title: 'Duration',
        dataIndex: 'duration',
        key: 'duration',
        render: (v) => formatHours(v),
    },
];

const SessionTable = ({ sessions = [] }) => (
    <Table
        size='small'
        rowKey={(_, i) => i}
        columns={sessionColumns}
        dataSource={sessions}
        pagination={false}
    />
);

const columns = [
    {
        title: 'Date',
        dataIndex: 'created',
        key: 'created',
        render: (v) => getDateString(v),
    },
    {
        title: 'Total hours',
        dataIndex: 'total_hours',
        key: 'total_hours',
        render: (v) => formatHours(v),
    },
    {
        title: 'Sessions',
        dataIndex: 'session_count',
        key: 'session_count',
    },
    {
        title: 'Last activity',
        dataIndex: 'updated',
        key: 'updated',
        render: (v) => getReadableDateFromTimeStamp(v),
    },
];

const MachineUsages = ({ machine }) => {
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, page_size: 10, total: 0 });

    const handleDownload = async (format = 'xlsx') => {
        setDownloading(true);
        try {
            const res = await http.get('/api/v1/machines/usages/download/', {
                params: { machine: machine.id, file_format: format },
                responseType: 'blob',
            });
            // filename comes from the backend Content-Disposition header
            const disposition = res.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `machine_usage.${format}`;
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            message.error('Failed to download usage data');
        } finally {
            setDownloading(false);
        }
    };

    useEffect(() => {
        let active = true;
        setLoading(true);
        http.get('/api/v1/machines/usages/', {
            params: { machine: machine.id, page: pagination.page, page_size: pagination.page_size },
        })
            .then((res) => {
                if (!active) return;
                setDataSource(res.data.results);
                setPagination((prev) => ({ ...prev, total: res.data.count }));
            })
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [machine.id, pagination.page, pagination.page_size]);

    return (
        <Card
            title={<span style={{ fontWeight: 'bold', fontSize: '30px' }}>Machine usage</span>}
            extra={
                <Dropdown
                    trigger={['click']}
                    disabled={dataSource.length === 0}
                    menu={{
                        items: [
                            { key: 'xlsx', label: 'Excel (.xlsx)' },
                            { key: 'csv', label: 'CSV (.csv)' },
                        ],
                        onClick: ({ key }) => handleDownload(key),
                    }}
                >
                    <Button
                        type='primary'
                        icon={<DownloadOutlined />}
                        loading={downloading}
                        disabled={dataSource.length === 0}
                    >
                        Download <DownOutlined />
                    </Button>
                </Dropdown>
            }
            // edge-to-edge table; head inline padding matches table cell inline padding (16px)
            // so the title aligns horizontally with the first column text
            bodyStyle={{ padding: 0 }}
            headStyle={{ paddingInline: 16 }}
        >
            <Alert
                type='warning'
                banner
                showIcon
                message='Session data is removed after 90 days. Download it if you want to keep a copy.'
            />
            <Table
                rowKey='id'
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                expandable={{
                    expandedRowRender: (record) => <SessionTable sessions={record.sessions} />,
                    rowExpandable: (record) => (record.sessions?.length || 0) > 0,
                }}
                pagination={{
                    current: pagination.page,
                    pageSize: pagination.page_size,
                    total: pagination.total,
                    showSizeChanger: true,
                    onChange: (page, page_size) =>
                        setPagination((prev) => ({ ...prev, page, page_size })),
                }}
            />
        </Card>
    );
};

export default MachineUsages;
